import {
  GridColDef,
  GridActionsCellItem,
  GridActionsColDef,
} from '@mui/x-data-grid';
import { Paper, IconButton, Grid, Tooltip, SpeedDial } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
  HideImage,
  Image,
} from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import DataList from '../../../components/DataList';
import useCheckPermission from '../../../hooks/useCheckPermission';
import useConfirmDeleteDialog from '../../../hooks/useConfirmDeleteDialog';
import parkingPlaceService from '../../../services/parking/parkingPlaceService';
import { translateParkingPlaceType } from '../../../utils/enumTranslaters';
import { ParkingPlaceType } from '../../../types/ParkingPlaceType';

export interface ParkingPlace {
  id: string;
  imageId: string;
  name: string;
}

const ParkingPlacesPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();

  const { checkPermission } = useCheckPermission();

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    parkingPlaceService
      .list()
      .then((response) => {
        if (!response.hasError) setRows(response.records);
        else
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const getActions = (params: GridRowParams) => {
    var actions: any[] = [];

    let readonly = !checkPermission(['EditParkingPlace']);
    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="View">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'View' : 'Edit'}
        onClick={() => navigate(`/parkingPlace/edit/${params.row.id}`)}
      />
    );

    if (checkPermission(['DeleteParkingPlace'])) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Delete">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() =>
            setParams({
              open: true,
              name: 'Are you sure to delete this item?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    { field: 'name', headerName: 'Megnevezés', flex: 100 },
    { field: 'zoneName', headerName: 'Zóna', flex: 100 },
    { field: 'latitude', headerName: 'Szélesség', flex: 100 },
    { field: 'longitude', headerName: 'Hosszúság', flex: 100 },
    {
      field: 'type',
      headerName: 'Típus',
      flex: 100,
      valueFormatter(params) {
        return translateParkingPlaceType(params.value as ParkingPlaceType);
      },
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 20,
      getActions: getActions,
    } as any,
  ];

  const handleDelete = (id: number) => {
    parkingPlaceService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Sikeres törlés!', {
          variant: 'success',
        });
        setRows(rows.filter((row) => row.id !== id));
      }
    });
  };

  return (
    <Paper>
      <Grid container sx={{
        p: 3
      }}>
        <Grid size={12} sx={{
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <h2>Parkoló helyek</h2>
        </Grid>
        {checkPermission(['CreateParkingPlace']) && (
          <Grid
            container
            size={12}
            sx={{
              p: 2,
              justifyContent: "end"
            }}>
            <Grid sx={{ display: { xs: 'none', sm: 'fixed' } }}>
              <Tooltip title="Parkoló hely létrehozása">
                <IconButton
                  component={RouterLink}
                  to={`/parkingPlace/create`}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList rows={rows} columns={columns} />
        </Grid>
        <ConfirmDeleteDialog />{' '}
        <SpeedDial
          ariaLabel="Okos kereső"
          onClick={() => {
            navigate(`/parkingPlace/create`);
          }}
          sx={{
            display: { xs: 'fixed', sm: 'none' },
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          icon={<AddIcon />}
        ></SpeedDial>
      </Grid>
    </Paper>
  );
};

export default ParkingPlacesPage;
